import React, { useRef, useState, useEffect } from "react";
import "./RichTextEditor.css";

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [selectedColor, setSelectedColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [selectedHeading, setSelectedHeading] = useState("p");

  // Update editor content when value prop changes
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  // Function to execute commands like Bold, Italic, etc.
  const applyCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
  };

  // Function to handle heading selection
  const handleHeading = (event) => {
    const heading = event.target.value;
    setSelectedHeading(heading);
    applyCommand("formatBlock", heading);
  };

  // Function to ensure "Enter" always resets to paragraph and updates select dropdown
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      setTimeout(() => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          let parentNode = selection.getRangeAt(0).commonAncestorContainer;

          // Traverse up to find the nearest element
          while (parentNode && parentNode.nodeType === 3) {
            parentNode = parentNode.parentNode;
          }

          // Do NOT reset to paragraph if inside a list
          if (
            parentNode &&
            (parentNode.tagName === "OL" || parentNode.tagName === "UL")
          ) {
            return;
          }

          // Only reset to paragraph if the selected heading is NOT 'p'
          if (selectedHeading !== "p") {
            applyCommand("formatBlock", "p");
            setSelectedHeading("p"); // Update dropdown
          }
        }
      }, 0);
    }
  };

  // Handle content changes
  const handleInput = () => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  return (
    <div className="editor-container">
      {/* Toolbar */}
      <div className="toolbar">
        {/* Headings */}
        <div className="tooltip">
          <select
            onChange={handleHeading}
            value={selectedHeading}
            className="tooltip-btn"
          >
            <option value="p">Paragraph</option>
            <option value="h1">H1</option>
            <option value="h2">H2</option>
            <option value="h3">H3</option>
            <option value="h4">H4</option>
            <option value="h5">H5</option>
            <option value="h6">H6</option>
          </select>
          <span className="tooltip-text">Select Heading</span>
        </div>

        {/* Formatting Buttons */}
        <div className="tooltip">
          <button className="tooltip-btn" onClick={() => applyCommand("bold")}>
            <b>B</b>
          </button>
          <span className="tooltip-text">Bold (Ctrl+B)</span>
        </div>

        <div className="tooltip">
          <button
            className="tooltip-btn"
            onClick={() => applyCommand("italic")}
          >
            <i>I</i>
          </button>
          <span className="tooltip-text">Italic (Ctrl+I)</span>
        </div>

        <div className="tooltip">
          <button
            className="tooltip-btn"
            onClick={() => applyCommand("underline")}
          >
            <u>U</u>
          </button>
          <span className="tooltip-text">Underline (Ctrl+U)</span>
        </div>

        <div className="tooltip">
          <button
            className="tooltip-btn"
            onClick={() => applyCommand("strikeThrough")}
          >
            <s>S</s>
          </button>
          <span className="tooltip-text">Strikethrough</span>
        </div>

        {/* Font Color */}
        <div className="tooltip">
          <input
            type="color"
            value={selectedColor}
            onChange={(e) => {
              setSelectedColor(e.target.value);
              applyCommand("foreColor", e.target.value);
            }}
            className="tooltip-btn"
          />
          <span className="tooltip-text">Font Color</span>
        </div>

        {/* Background Color */}
        <div className="tooltip">
          <input
            type="color"
            value={bgColor}
            onChange={(e) => {
              setBgColor(e.target.value);
              applyCommand("hiliteColor", e.target.value);
            }}
            className="tooltip-btn"
          />
          <span className="tooltip-text">Background Color</span>
        </div>

        {/* Link Insertion */}
        <div className="tooltip">
          <button
            className="tooltip-btn"
            onClick={() => {
              const url = prompt("Enter the URL");
              if (url) applyCommand("createLink", url);
            }}
          >
            🔗
          </button>
          <span className="tooltip-text">Insert Link</span>
        </div>

        {/* List Options */}
        <div className="tooltip">
          <button
            className="tooltip-btn"
            onClick={() => applyCommand("insertUnorderedList")}
          >
            • List
          </button>
          <span className="tooltip-text">Bulleted List</span>
        </div>

        <div className="tooltip">
          <button
            className="tooltip-btn"
            onClick={() => applyCommand("insertOrderedList")}
          >
            1. List
          </button>
          <span className="tooltip-text">Numbered List</span>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="editor"
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        data-placeholder={placeholder || "Start typing..."}
      ></div>
    </div>
  );
};

export default RichTextEditor;
